import { Controller } from "@hotwired/stimulus"
import { timerUnit } from "../helpers/locale"

// The landing page's copy of onboarding slide 13's demo (home/_ways_demo):
// the add-expense sheet looping Scan -> Speak -> Type. Everything below the
// lifecycle is onboarding_controller.js's ways code, copied as is; the only
// change is in selectWay, which has no slide to check here.
//
// The loop runs only while the demo is on screen and stops when it scrolls
// away, so nothing ticks below the fold (in_view_animation_controller).
export default class extends Controller {
  static targets = ["waysDemo", "waysField", "waysFieldBox", "waysTyped", "waysCaret",
                    "waysPlaceholder", "waysCount", "waysPhotoRow", "waysTakePhoto",
                    "waysRings", "waysMascotOff", "waysMascotOn", "waysStatus", "waysTimer",
                    "waysLoading", "waysAnalyzing", "waysCard", "waysStrings"]

  connect() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!this.waysRun) this.runWaysDemo(this.waysCurrent ?? 0)
        } else {
          this.cancelWaysDemo()
        }
      })
    })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.cancelWaysDemo()
  }

  // ── Copied from onboarding_controller.js (Slide 13 · Three ways in) ──
  static WAYS = ["snap", "speak", "type"]
  static WAYS_RECORDING_SECS = 15   // RECORDING_DURATION_SECS, voice_modal_controller.js

  runWaysDemo(startAt = 0) {
    if (!this.hasWaysDemoTarget) return
    this.cancelWaysDemo()
    const run = {}
    this.waysRun = run
    this.waysTimers = []

    const loop = async () => {
      let i = startAt
      while (this.waysRun === run) {
        const way = this.constructor.WAYS[i]
        this.#selectWayTab(i)
        this.#resetWaysSlot(i)
        await this.#waysSleep(700)
        if (this.waysRun !== run) return
        if (way === "snap")       await this.#playSnap(run)
        else if (way === "speak") await this.#playSpeak(run)
        else                      await this.#playType(run)
        if (this.waysRun !== run) return
        await this.#convertWay(way, run)
        i = (i + 1) % this.constructor.WAYS.length
      }
    }
    loop()
  }

  cancelWaysDemo() {
    this.waysRun = null
    this.waysSelecting = false
    if (this.waysSettleFrame) {
      cancelAnimationFrame(this.waysSettleFrame)
      this.waysSettleFrame = null
    }
    if (this.waysTimers) {
      this.waysTimers.forEach(id => clearTimeout(id))
      this.waysTimers = []
    }
    if (this.waysTypeFrame) {
      cancelAnimationFrame(this.waysTypeFrame)
      this.waysTypeFrame = null
    }
    if (!this.hasWaysDemoTarget) return
    this.#resetWaysSlot(this.waysCurrent ?? 0)
  }

  // A deck swipe (dock taps come in through replayWay). The loop's own tab
  // moves are ignored, and so is the whole of the smooth scroll they start:
  // expense-deck stops reading scroll events 550ms after it scrolls, but the
  // scroll itself can outlast that, and while it is still passing a panel the
  // deck rounds to that panel and reports it — a change of mind the user
  // never had. waysSelecting stays up until the deck has actually arrived.
  selectWay(event) {
    // expense-deck also reports its first apply, on connect: nothing to replay
    // until the demo has started.
    if (!this.waysRun) return
    if (this.waysSelecting) return
    const index = Number(event.detail?.index)
    if (Number.isNaN(index)) return
    if (index === this.waysCurrent) return
    this.runWaysDemo(index)
  }

  // A tap on a dock tab, a panel or a photo tile. Always restarts, even on the
  // way in already playing: a tap should visibly do something.
  replayWay(event) {
    const index = this.constructor.WAYS.indexOf(event.currentTarget.dataset.way)
    if (index < 0) return
    this.runWaysDemo(index)
  }

  #selectWayTab(index) {
    this.waysCurrent = index

    // The thumb's place, by formula rather than by measurement: every tab is
    // 76px and the bar pads 8px (the dock's own w-[76px] and px-2). On iOS
    // WebKit the measured translate expense-deck sets was seen left on Scan
    // with Speak or Type active; onboarding.css pins the thumb to this
    // variable instead, mirrored by inset-inline-start in RTL.
    const dock = this.waysDemoTarget.querySelector("[data-expense-deck-target='dock']")
    if (dock) dock.style.setProperty("--ways-thumb-x", `${8 + 76 * index}px`)

    const deck = this.application.getControllerForElementAndIdentifier(this.waysDemoTarget, "expense-deck")
    if (!deck) return
    this.waysSelecting = true
    deck.selectIndex(index)

    // Hold the flag until the deck has physically landed on the panel (or a
    // generous ceiling, for a deck that never scrolls, e.g. reduced motion).
    const el = this.waysDemoTarget.querySelector("[data-expense-deck-target='deck']")
    const width = el ? el.clientWidth : 0
    const startedAt = performance.now()
    const settle = () => {
      if (this.waysCurrent !== index) return            // superseded by a newer move
      const at = el ? Math.abs(el.scrollLeft) : 0
      const arrived = !width || Math.abs(at - width * index) <= 2
      if (arrived || performance.now() - startedAt > 1500) {
        this.waysSelecting = false
        // Apply once more now that layout has settled: the thumb is placed
        // off measured rects, and a rect read mid-scroll on WebKit has been
        // seen to leave it on the previous tab. Same index, so the tabchange
        // this raises is ignored by selectWay.
        deck.selectIndex(index)
        return
      }
      this.waysSettleFrame = requestAnimationFrame(settle)
    }
    this.waysSettleFrame = requestAnimationFrame(settle)
  }

  #waysSleep(ms) {
    return new Promise(resolve => {
      const id = setTimeout(resolve, ms)
      if (this.waysTimers) this.waysTimers.push(id)
    })
  }

  // The slot back to its input for the way in showing: the tiles on Scan, an
  // empty field otherwise, skeleton and cards away, mascot at rest.
  #resetWaysSlot(index) {
    const onScan = index === 0
    if (this.hasWaysFieldTarget)    this.waysFieldTarget.classList.toggle("hidden", onScan)
    if (this.hasWaysPhotoRowTarget) this.waysPhotoRowTarget.classList.toggle("hidden", !onScan)
    if (this.hasWaysLoadingTarget)  this.waysLoadingTarget.classList.add("hidden")
    this.waysCardTargets.forEach(card => card.classList.add("hidden"))

    if (this.hasWaysTypedTarget)       this.waysTypedTarget.textContent = ""
    if (this.hasWaysCaretTarget)       this.waysCaretTarget.classList.add("hidden")
    if (this.hasWaysPlaceholderTarget) this.waysPlaceholderTarget.classList.remove("hidden")
    if (this.hasWaysCountTarget)       this.waysCountTarget.textContent = "0 / 200"
    if (this.hasWaysFieldBoxTarget)    this.waysFieldBoxTarget.classList.remove("is-live")
    if (this.hasWaysTakePhotoTarget)   this.waysTakePhotoTarget.classList.remove("is-pressed")

    this.waysDemoTarget.classList.remove("is-done")
    this.#stopListening()
  }

  #stopListening() {
    if (this.waysTimerTick) {
      clearInterval(this.waysTimerTick)
      this.waysTimerTick = null
    }
    if (this.hasWaysRingsTarget)     this.waysRingsTarget.classList.add("hidden")
    if (this.hasWaysMascotOnTarget)  this.waysMascotOnTarget.classList.add("hidden")
    if (this.hasWaysMascotOffTarget) {
      this.waysMascotOffTarget.classList.remove("hidden", "is-tapped")
    }
    if (this.hasWaysStatusTarget) this.waysStatusTarget.textContent = this.waysStatusTarget.dataset.idle || ""
    if (this.hasWaysTimerTarget) {
      this.waysTimerTarget.classList.add("hidden")
      this.waysTimerTarget.textContent = ""
    }
  }

  // Types `text` into the field over `ms`, then holds the caret for a beat.
  async #typeIntoField(text, ms, run) {
    if (this.hasWaysPlaceholderTarget) this.waysPlaceholderTarget.classList.add("hidden")
    if (this.hasWaysCaretTarget)       this.waysCaretTarget.classList.remove("hidden")
    if (this.hasWaysFieldBoxTarget)    this.waysFieldBoxTarget.classList.add("is-live")

    const chars = Array.from(text)
    await new Promise(resolve => {
      const startedAt = performance.now()
      const tick = (now) => {
        if (this.waysRun !== run) { resolve(); return }
        const t = Math.min(1, (now - startedAt) / ms)
        const shown = Math.round(t * chars.length)
        if (this.hasWaysTypedTarget) this.waysTypedTarget.textContent = chars.slice(0, shown).join("")
        if (this.hasWaysCountTarget) this.waysCountTarget.textContent = `${shown} / 200`
        if (t < 1) this.waysTypeFrame = requestAnimationFrame(tick)
        else resolve()
      }
      this.waysTypeFrame = requestAnimationFrame(tick)
    })
    await this.#waysSleep(350)
    if (this.waysRun !== run) return
    if (this.hasWaysCaretTarget) this.waysCaretTarget.classList.add("hidden")
  }

  // Scan: the Take photo tile is pressed. The sheet shows nothing between the
  // camera and the skeleton, so neither does the demo.
  async #playSnap(run) {
    await this.#waysSleep(500)
    if (this.waysRun !== run) return
    const tile = this.hasWaysTakePhotoTarget ? this.waysTakePhotoTarget : null
    if (tile) tile.classList.add("is-pressed")
    await this.#waysSleep(220)
    if (tile) tile.classList.remove("is-pressed")
    if (this.waysRun !== run) return
    await this.#waysSleep(300)
  }

  // Speak: the mascot is tapped, the rings pulse, the timer runs, the words
  // land in the field, then the mascot rests again.
  async #playSpeak(run) {
    await this.#waysSleep(400)
    if (this.waysRun !== run) return
    if (this.hasWaysMascotOffTarget) this.waysMascotOffTarget.classList.add("is-tapped")
    await this.#waysSleep(160)
    if (this.hasWaysMascotOffTarget) this.waysMascotOffTarget.classList.remove("is-tapped")
    if (this.waysRun !== run) return

    if (this.hasWaysMascotOffTarget) this.waysMascotOffTarget.classList.add("hidden")
    if (this.hasWaysMascotOnTarget)  this.waysMascotOnTarget.classList.remove("hidden")
    if (this.hasWaysRingsTarget)     this.waysRingsTarget.classList.remove("hidden")
    if (this.hasWaysStatusTarget)    this.waysStatusTarget.textContent = this.waysStatusTarget.dataset.listening || ""
    // The sheet's own countdown: RECORDING_DURATION_SECS in
    // voice_modal_controller.js, shown with the locale's second unit.
    let remaining = this.constructor.WAYS_RECORDING_SECS
    if (this.hasWaysTimerTarget) {
      this.waysTimerTarget.textContent = `${remaining}${timerUnit()}`
      this.waysTimerTarget.classList.remove("hidden")
    }
    this.waysTimerTick = setInterval(() => {
      remaining = Math.max(0, remaining - 1)
      if (this.hasWaysTimerTarget) this.waysTimerTarget.textContent = `${remaining}${timerUnit()}`
    }, 1000)

    const text = this.hasWaysStringsTarget ? (this.waysStringsTarget.dataset.speak || "") : ""
    await this.#typeIntoField(text, 1900, run)
    if (this.waysRun !== run) return
    this.#stopListening()
    await this.#waysSleep(450)
  }

  // Type: the words are typed in, plain digits and all.
  async #playType(run) {
    await this.#waysSleep(400)
    if (this.waysRun !== run) return
    const text = this.hasWaysStringsTarget ? (this.waysStringsTarget.dataset.type || "") : ""
    await this.#typeIntoField(text, 1300, run)
    await this.#waysSleep(350)
  }

  // What the sheet does with it, below the wallpaper: the analyzing skeleton
  // appears, then the confirm card lands, and the mascot is pleased with it.
  // The input stays as it is above, so what went in and what came out are on
  // screen together.
  async #convertWay(way, run) {
    if (this.hasWaysAnalyzingTarget) {
      this.waysAnalyzingTarget.textContent = this.waysAnalyzingTarget.dataset[way] || ""
    }
    if (this.hasWaysLoadingTarget) this.waysLoadingTarget.classList.remove("hidden")
    await this.#waysSleep(1300)
    if (this.waysRun !== run) return
    if (this.hasWaysLoadingTarget) this.waysLoadingTarget.classList.add("hidden")

    const card = this.waysCardTargets.find(el => el.dataset.way === way)
    if (card) {
      card.classList.remove("hidden")
      // Re-arm the entrance keyframe on a card that has played before.
      const inner = card.querySelector(".voice-card-in")
      if (inner) {
        inner.style.animation = "none"
        void inner.offsetWidth
        inner.style.animation = ""
      }
    }
    this.waysDemoTarget.classList.add("is-done")
    await this.#waysSleep(2600)
  }
}
